// eslint-disable-react/prop-types
import { useState, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Column } from "react-table";
import Button from "components/atoms/Button/Button";

import { getAddressBook } from "api";
import LocationFilter from "components/molecules/LocationFilter/LocationFilter";
import GeocodedFilter from "components/molecules/GeocodedFilter/GeocodedFilter";
import testIds from "constants/testIds";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import usePermissions from "hooks/usePermissions";
import useTableRequest from "hooks/useTableRequest";
import getLabel from "helpers/getLabel";
import EditAddressBook from "./EditAddressBook";
import MissingGeolocationIndicator from "./MissingGeolocationIndicator";

export default function AddressTable() {
  const intl = useIntl();

  const { permissions } = usePermissions();
  const { address_availability, co2_emission_enabled } = JSON.parse(permissions ?? "{}").company_shipper_settings;

  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const columns = useMemo<Column[]>(
    () => [
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.companyName" defaultMessage="Company Name" />
          </span>
        ),
        sortable: false,
        accessor: "company_name",
        maxWidth: 150,
        Cell: ({ row }: { row: { original: { company_name: string; is_geocoded?: boolean } } }) => {
          const { company_name, is_geocoded } = row.original;
          return (
            <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
              <p
                style={{
                  margin: 0,
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {company_name}
              </p>
              {co2_emission_enabled && is_geocoded === false && <MissingGeolocationIndicator />}
            </div>
          );
        },
      },
      {
        Header: (
          <span className="pl-9 text-nowrap">
            {intl.formatMessage(
              {
                id: "general.paymentLocation",
                defaultMessage: "PAYMENT LOCATION",
              },
              {
                paymentLocation: getLabel("Payment location", "uppercase"),
              },
            )}
          </span>
        ),
        sortable: true,
        accessor: "payment_location",
        Cell: ({ value }: { value: string }) => <p className="pl-9 pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.address" defaultMessage="Address" />
          </span>
        ),
        accessor: "address_details",
        id: "address",
        sortable: false,
        maxWidth: 200,
        Cell: ({ value }: { value: { address_line_1: string } }) => {
          const { address_line_1 } = value;
          return (
            <div style={{ width: "70%" }}>
              <p
                style={{
                  minWidth: "150px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                className="pt-8 pb-8 mb-0"
              >
                {address_line_1}
              </p>
            </div>
          );
        },
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.postalCode" defaultMessage="ZIP/Postal Code" />
          </span>
        ),
        accessor: "address_details",
        id: "postalCode",
        sortable: false,
        Cell: ({ value }: { value: { postal_code: string } }) => {
          const { postal_code } = value;
          return <p className="pt-8 pb-8 mb-0">{postal_code}</p>;
        },
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.city" defaultMessage="City" />
          </span>
        ),
        accessor: "address_details",
        id: "city",
        sortable: true,
        Cell: ({ value }: { value: { city: string } }) => {
          const { city } = value;
          return <p className="pt-8 pb-8 mb-0">{city}</p>;
        },
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.state" defaultMessage="State" />
          </span>
        ),
        accessor: "address_details",
        id: "state",
        sortable: false,
        Cell: ({ value }: { value: { state: string } }) => {
          const { state } = value;
          return <p className="pt-8 pb-8 mb-0">{state}</p>;
        },
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.country" defaultMessage="Country" />
          </span>
        ),
        accessor: "address_details",
        id: "country",
        sortable: true,
        Cell: ({ value }: { value: { country_name: string } }) => {
          const { country_name } = value;
          return <p className="pt-8 pb-8 mb-0">{country_name}</p>;
        },
      },
      {
        Header: (
          <span className="text-nowrap">
            <FormattedMessage id="general.lastModified" defaultMessage="Last modified" />
          </span>
        ),
        sortable: false,
        accessor: "last_modified",
        maxWidth: 150,
        Cell: ({ value }: { value: string }) => <p className="pt-8 pb-8 mb-0">{value}</p>,
      },
    ],
    [co2_emission_enabled],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const { instance, fetching, totalData, refetch } = useTableRequest(getAddressBook, columns, {
    hiddenColumns: address_availability !== "Payment Location" ? ["payment_location"] : [],
    params: {},
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location }),
      ...(globalFilter.geocoded?.length && { geocoded: globalFilter.geocoded }),
    }),
  });

  const openAddAddressDialog = () => {
    setSelectedId(null);
    setShowAddressDialog(true);
  };

  const openEditAddressDialog = (id: number) => {
    setSelectedId(id);
    setShowAddressDialog(true);
  };

  const closeEditAddressDialog = () => {
    setSelectedId(null);
    setShowAddressDialog(false);
  };

  const handleFilterSelect = (value: string) => {
    instance.setGlobalFilter({
      ...instance.state.globalFilter,
      payment_location: value,
    });
  };

  const handleGeocodedFilterSelect = (value: string) => {
    instance.setGlobalFilter({
      ...instance.state.globalFilter,
      geocoded: value,
    });
  };

  return (
    <>
      <EditAddressBook
        refetch={refetch}
        showAddressDialog={showAddressDialog}
        closeEditAddressDialog={closeEditAddressDialog}
        selectedId={selectedId}
        allowPaymentLocation={address_availability === "Payment Location"}
      />

      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row) => openEditAddressDialog(row.original.id)}
        headerRight={
          <div className="d-flex flex-wrap flex-md-row flex-column  justify-content-between mt-3 w-100">
            <div className="mb-3 mb-md-0 d-flex justify-content-between w-100">
              <GlobalTableSearch
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                filterKey="query"
                eventContext="Address Book"
              />

              <Button
                className="ml-3 rounded-pill"
                onClick={openAddAddressDialog}
                data-testid={testIds.ADDRESS_BOOK_ADD_BUTTON}
              >
                <FormattedMessage id="settings.address-book.addAddress" defaultMessage="Add address" />
              </Button>
            </div>
            <div className="d-flex align-items-center gap-sm">
              {address_availability === "Payment Location" && <LocationFilter onChange={handleFilterSelect} />}
              {co2_emission_enabled && <GeocodedFilter onChange={handleGeocodedFilterSelect} />}
            </div>
          </div>
        }
      />
    </>
  );
}
